import { baseClient } from "./baseRequest";

export type CreativeAspect = "1:1" | "4:5" | "9:16" | "16:9";
export type CreativeFinish = "oak" | "walnut" | "ebony" | "any";
export type CreativeOutputType = "images" | "video" | "klydo";

export type CreativeOutputRequest =
  | { type: "images"; count: number; aspect: CreativeAspect }
  | { type: "video"; durationSec: number; aspect: CreativeAspect }
  // A seamless 1080x1080 loop that plays on the clock screen.
  | { type: "klydo"; durationSec: number };

export type CreateCreativeJobRequest = {
  description: string;
  output: CreativeOutputRequest;
  finish: CreativeFinish;
  collectionId?: string;
  klydoIds?: string[];
  /** URLs returned by `uploadInputImage`. */
  inputImages?: string[];
  /** URL returned by `uploadAudio`. The output is exactly as long as the track. */
  audioUrl?: string;
};

export type CreativeOutputKind = "image" | "video" | "json";

export type CreativeJobOutput = {
  kind: CreativeOutputKind;
  label: string;
  url: string;
  width?: number;
  height?: number;
  durationSec?: number;
};

export type CreateCreativeJobResponse = {
  jobId: string;
  /** Final public URLs. They 404 until the job is done. */
  outputs: CreativeJobOutput[];
};

export type CreativeJobStatus =
  | "queued"
  | "running"
  | "rendering"
  | "uploading"
  | "done"
  | "failed";

export type CreativeRequester = {
  uid: string;
  email?: string;
  role?: string;
};

export type CreativeJob = {
  id: string;
  /** ISO date strings. */
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  requestedBy: CreativeRequester;
  request: {
    description: string;
    output: CreativeOutputRequest;
    finish: CreativeFinish;
    collection?: { id: string; name: string };
    inputImages: string[];
    audioUrl?: string;
  };
  status: CreativeJobStatus;
  progress?: string;
  outputs: CreativeJobOutput[];
  error?: string;
  /** Claude spend for this job. */
  costUsd?: number;
  /** Klydo animations: the theme colors that go with the loop. */
  klydoTheme?: Record<string, string>;
  /** Notes from the automatic quality check when the job finished without fixing them. */
  warnings?: string[];
};

export type CreativeStudioApiClient = {
  createJob: (
    request: CreateCreativeJobRequest,
  ) => Promise<CreateCreativeJobResponse>;
  /**
   * One file per request: Cloud Run rejects request bodies over 32 MB, and a
   * reference image can be up to 15 MB.
   */
  uploadInputImage: (file: File) => Promise<string>;
  /** A soundtrack for a video or klydo animation (up to 15 MB). */
  uploadAudio: (file: File) => Promise<string>;
  getJobs: (limit?: number) => Promise<CreativeJob[]>;
  getJob: (id: string) => Promise<CreativeJob>;
};

const creativeStudioApiClient: CreativeStudioApiClient = {
  createJob: async (request) =>
    baseClient.post<CreateCreativeJobResponse>({
      url: "/media-generation/creative",
      body: request,
    }),
  uploadInputImage: async (file) => {
    const body = new FormData();
    body.append("files", file);
    const { urls } = await baseClient.post<{ urls: string[] }>({
      url: "/media-generation/creative/inputs",
      body,
    });
    return urls[0];
  },
  uploadAudio: async (file) => {
    const body = new FormData();
    body.append("file", file);
    const { url } = await baseClient.post<{ url: string }>({
      url: "/media-generation/creative/audio",
      body,
    });
    return url;
  },
  getJobs: async (limit = 50) => {
    const { jobs } = await baseClient.get<{ jobs: CreativeJob[] }>({
      url: "/media-generation/creative/jobs",
      params: { limit },
    });
    return jobs;
  },
  getJob: async (id) =>
    baseClient.get<CreativeJob>({
      url: `/media-generation/creative/jobs/${encodeURIComponent(id)}`,
    }),
};

export default creativeStudioApiClient;
